import { alpha, createTheme, darken, lighten } from '@mui/material/styles';
import {
  FONT_BODY,
  FONT_DATA,
  FONT_DISPLAY,
  GroundTokens,
  defaultGroundForMode,
  getGround,
} from './theme/grounds';

/**
 * Deeliver Admin Theme
 *
 * Built from a ground (see ./theme/grounds.ts): the six colour surfaces the
 * admin can sit on. Everything below reads tokens off the ground, so a ground
 * swap re-themes every MUI component without touching a page.
 *
 * The skin, in short:
 * - Archivo for display + body, JetBrains Mono for data, labels and eyebrows.
 * - Panels are flat: a panel surface with a soft hairline, no shadow, no lift.
 * - One accent (the ember) for primary actions, focus and the active nav row.
 * - The app bar stays Deeliver blue on every ground — it's brand, not surface.
 */

export const APP_BAR_GRADIENT = 'linear-gradient(105deg, #0a4f9c 0%, #0d66bb 60%, #1a78d6 125%)';

/** Mono label: uppercase, tracked, small — the prototype's `.eyebrow` / `.band` / `.kpi .lab`. */
export const dataLabel = {
  fontFamily: FONT_DATA,
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.13em',
  textTransform: 'uppercase' as const,
};

export const createDeeliverTheme = (groundOrMode: GroundTokens | 'light' | 'dark' = 'light') => {
  const g: GroundTokens =
    typeof groundOrMode === 'string' ? getGround(defaultGroundForMode(groundOrMode)) : groundOrMode;
  const mode = g.mode;
  const dark = mode === 'dark';
  // Neutral "info" blue: the skin has no cool tone of its own, and a category
  // chip in ember would read as a state rather than a label.
  const info = dark ? '#5FA8FF' : '#1D6FD1';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: g.ember,
        light: lighten(g.ember, 0.18),
        dark: darken(g.ember, 0.18),
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: g.tx,
        light: g.tx2,
        dark: g.tx,
        contrastText: g.canvas,
      },
      background: {
        default: g.canvas,
        paper: g.panel,
      },
      text: {
        primary: g.tx,
        secondary: g.tx2,
        disabled: g.tx3,
      },
      divider: g.lineSoft,
      error: {
        main: g.crit,
        light: lighten(g.crit, 0.2),
        dark: darken(g.crit, 0.2),
        contrastText: '#FFFFFF',
      },
      warning: {
        main: g.warn,
        light: lighten(g.warn, 0.2),
        dark: darken(g.warn, 0.2),
        contrastText: dark ? '#1A1512' : '#FFFFFF',
      },
      success: {
        main: g.ok,
        light: lighten(g.ok, 0.2),
        dark: darken(g.ok, 0.2),
        contrastText: dark ? '#0A1512' : '#FFFFFF',
      },
      info: {
        main: info,
        light: lighten(info, 0.2),
        dark: darken(info, 0.2),
        contrastText: '#FFFFFF',
      },
      action: {
        hover: g.hover,
        selected: g.emberBg,
        disabled: g.tx3,
        disabledBackground: g.raised,
      },
    },
    typography: {
      fontFamily: FONT_BODY,
      h1: { fontFamily: FONT_DISPLAY, fontStretch: '125%', fontSize: '2.25rem', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em' },
      h2: { fontFamily: FONT_DISPLAY, fontStretch: '118%', fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' },
      h3: { fontFamily: FONT_DISPLAY, fontStretch: '118%', fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' },
      h4: { fontFamily: FONT_DISPLAY, fontStretch: '118%', fontSize: '1.375rem', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em' },
      h5: { fontFamily: FONT_DISPLAY, fontStretch: '118%', fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h6: { fontFamily: FONT_DISPLAY, fontStretch: '110%', fontSize: '0.97rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.008em' },
      subtitle1: { fontFamily: FONT_DISPLAY, fontStretch: '110%', fontSize: '0.97rem', fontWeight: 700, lineHeight: 1.25 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.55 },
      body2: { fontSize: '0.84rem', fontWeight: 400, lineHeight: 1.5 },
      button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
      caption: { fontFamily: FONT_DATA, fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.5 },
      overline: { ...dataLabel, lineHeight: 1.6 },
    },
    shape: {
      borderRadius: 6,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: g.canvas,
            color: g.tx,
            WebkitFontSmoothing: 'antialiased',
          },
          ':focus-visible': {
            outline: `2px solid ${g.ember}`,
            outlineOffset: 2,
          },
          '::-webkit-scrollbar': { width: 9, height: 9 },
          '::-webkit-scrollbar-thumb': { background: g.line, borderRadius: 5 },
          '::-webkit-scrollbar-track': { background: 'transparent' },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            // Deeliver blue. Hardcoded on purpose: the banner is brand, so it
            // must NOT re-tint with the ground picker.
            background: APP_BAR_GRADIENT,
            color: '#FFFFFF',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: g.sunk,
            backgroundImage: 'none',
            borderRight: `1px solid ${g.lineSoft}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // MUI dark mode paints an elevation overlay gradient on Paper;
            // the skin's surfaces are flat.
            backgroundImage: 'none',
            borderRadius: 10,
          },
          outlined: {
            borderColor: g.lineSoft,
          },
          elevation1: { boxShadow: 'none', border: `1px solid ${g.lineSoft}` },
          elevation2: { boxShadow: 'none', border: `1px solid ${g.lineSoft}` },
          elevation3: { boxShadow: 'none', border: `1px solid ${g.line}` },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: 'none',
            border: `1px solid ${g.lineSoft}`,
            backgroundColor: g.panel,
            transition: 'border-color .15s',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 600,
            padding: '7px 14px',
            boxShadow: 'none',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: {
            backgroundColor: g.ember,
            color: '#FFFFFF',
            '&:hover': { backgroundColor: darken(g.ember, 0.12) },
          },
          outlined: {
            borderColor: g.line,
            color: g.tx,
            '&:hover': { borderColor: g.ember, backgroundColor: g.raised },
          },
          outlinedPrimary: {
            borderColor: g.emberLine,
            color: g.ember,
            '&:hover': { borderColor: g.ember, backgroundColor: g.emberBg },
          },
          text: {
            color: g.tx2,
            '&:hover': { color: g.tx, backgroundColor: g.raised },
          },
          textPrimary: {
            color: g.ember,
            '&:hover': { backgroundColor: g.emberBg },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            color: g.tx3,
            '&:hover': { color: g.tx, backgroundColor: g.raised },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
            fontSize: '0.72rem',
          },
          outlined: {
            borderColor: g.line,
            color: g.tx2,
          },
          filled: {
            backgroundColor: g.raised,
            color: g.tx,
          },
          colorPrimary: { backgroundColor: g.emberBg, color: g.ember, border: `1px solid ${g.emberLine}` },
          colorSuccess: { backgroundColor: g.okBg, color: g.ok, border: `1px solid ${g.okLine}` },
          colorWarning: { backgroundColor: g.warnBg, color: g.warn, border: `1px solid ${g.warnLine}` },
          colorError: { backgroundColor: alpha(g.crit, dark ? 0.14 : 0.1), color: g.crit, border: `1px solid ${alpha(g.crit, 0.35)}` },
          colorInfo: { backgroundColor: alpha(info, 0.12), color: info, border: `1px solid ${alpha(info, 0.3)}` },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              backgroundColor: g.raised,
              '& fieldset': { borderColor: g.line },
              '&:hover fieldset': { borderColor: g.emberLine },
              '&.Mui-focused fieldset': { borderColor: g.ember, borderWidth: '1px' },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: g.raised,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: g.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: g.emberLine },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: g.ember, borderWidth: '1px' },
          },
          input: {
            '&::placeholder': { color: g.tx3, opacity: 1 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: g.tx3,
            '&.Mui-focused': { color: g.ember },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': { color: g.ember },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: g.ember },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: g.tx3,
            '&.Mui-checked': { color: g.ember },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: g.tx3,
            '&.Mui-checked': { color: g.ember },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: g.panel,
            border: `1px solid ${g.line}`,
            borderRadius: 10,
            boxShadow: `0 18px 44px ${alpha(g.shade, dark ? 0.45 : 0.35)}`,
          },
          list: { padding: 5 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontSize: '0.84rem',
            color: g.tx2,
            '&:hover': { backgroundColor: g.raised, color: g.tx },
            '&.Mui-selected': {
              backgroundColor: g.emberBg,
              color: g.ember,
              '&:hover': { backgroundColor: g.emberBg },
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            '&.Mui-selected': {
              backgroundColor: g.emberBg,
              '&:hover': { backgroundColor: g.emberBg },
            },
            '&:hover': { backgroundColor: g.hover },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: g.tx2,
            minWidth: 40,
            '.Mui-selected &': { color: g.ember },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 600,
            fontSize: '0.875rem',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${g.lineSoft}`,
            color: g.tx,
            fontSize: '0.84rem',
          },
          head: {
            ...dataLabel,
            backgroundColor: g.panel,
            color: g.tx3,
            borderBottom: `1px solid ${g.line}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: g.hover },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: { color: g.tx2, fontFamily: FONT_DATA, fontSize: '0.75rem' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.84rem',
            color: g.tx3,
            minHeight: 40,
            '&:hover': { color: g.tx2 },
            '&.Mui-selected': { color: g.ember },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: {
            backgroundColor: g.ember,
            height: 2,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: `0 10px 30px ${alpha(g.ember, 0.35)}`,
          },
          primary: {
            backgroundColor: g.ember,
            '&:hover': { backgroundColor: darken(g.ember, 0.12) },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: g.panel,
            backgroundImage: 'none',
            border: `1px solid ${g.line}`,
            borderRadius: 10,
            boxShadow: `0 30px 80px ${alpha(g.shade, dark ? 0.6 : 0.4)}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: FONT_DISPLAY,
            fontStretch: '118%',
            fontSize: '1.2rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: g.lineSoft },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: g.panel,
            color: g.tx,
            border: `1px solid ${g.line}`,
            fontFamily: FONT_DATA,
            fontSize: '0.7rem',
            fontWeight: 500,
            borderRadius: 6,
            padding: '6px 10px',
            boxShadow: `0 12px 30px ${alpha(g.shade, dark ? 0.5 : 0.3)}`,
          },
          arrow: { color: g.panel },
        },
      },
      MuiBadge: {
        styleOverrides: {
          colorPrimary: { backgroundColor: g.ember },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 3,
            backgroundColor: g.track,
          },
          bar: {
            borderRadius: 3,
            backgroundColor: g.ember,
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: { color: g.ember },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: g.raised },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: g.panel,
            backgroundImage: 'none',
            border: `1px solid ${g.lineSoft}`,
            boxShadow: 'none',
            '&:before': { display: 'none' },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: g.panel,
            border: `1px solid ${g.line}`,
            boxShadow: `0 18px 44px ${alpha(g.shade, dark ? 0.45 : 0.35)}`,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: g.panel,
            border: `1px solid ${g.line}`,
          },
          option: {
            fontSize: '0.84rem',
            '&[aria-selected="true"]': { backgroundColor: `${g.emberBg} !important`, color: g.ember },
          },
        },
      },
      // react-admin notification (Snackbar) — keep the text readable on every ground.
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiSnackbarContent-root': {
              backgroundColor: g.panel,
              color: g.tx,
              border: `1px solid ${g.line}`,
              fontSize: '0.84rem',
              fontWeight: 500,
              borderRadius: 8,
              boxShadow: `0 14px 40px ${alpha(g.shade, 0.5)}`,
            },
            '& .MuiAlert-filledError, & .MuiAlert-standardError': {
              backgroundColor: g.crit,
              color: '#FFFFFF',
              '& .MuiAlert-icon': { color: '#FFFFFF' },
              '& .MuiAlert-message': { color: '#FFFFFF' },
            },
            '& .MuiAlert-filledSuccess, & .MuiAlert-standardSuccess': {
              backgroundColor: darken(g.ok, dark ? 0.35 : 0),
              color: '#FFFFFF',
              '& .MuiAlert-icon': { color: '#FFFFFF' },
            },
            '& .MuiAlert-filledWarning, & .MuiAlert-standardWarning': {
              backgroundColor: darken(g.warn, dark ? 0.3 : 0),
              color: '#FFFFFF',
              '& .MuiAlert-icon': { color: '#FFFFFF' },
            },
            '& .MuiAlert-filledInfo, & .MuiAlert-standardInfo': {
              backgroundColor: info,
              color: '#FFFFFF',
              '& .MuiAlert-icon': { color: '#FFFFFF' },
            },
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: g.panel,
            color: g.tx,
            fontSize: '0.84rem',
            fontWeight: 500,
            borderRadius: 8,
            minWidth: 288,
            maxWidth: 568,
          },
          message: { padding: '8px 0', color: g.tx },
          action: { marginRight: -4, color: g.tx },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
          filledError: { backgroundColor: g.crit, color: '#FFFFFF' },
          filledWarning: { backgroundColor: darken(g.warn, dark ? 0.3 : 0), color: '#FFFFFF' },
          filledInfo: { backgroundColor: info, color: '#FFFFFF' },
          filledSuccess: { backgroundColor: darken(g.ok, dark ? 0.35 : 0), color: '#FFFFFF' },
          standardError: { backgroundColor: alpha(g.crit, dark ? 0.14 : 0.08), color: g.crit, border: `1px solid ${alpha(g.crit, 0.35)}` },
          standardWarning: { backgroundColor: g.warnBg, color: g.warn, border: `1px solid ${g.warnLine}` },
          standardInfo: { backgroundColor: alpha(info, 0.12), color: info, border: `1px solid ${alpha(info, 0.3)}` },
          standardSuccess: { backgroundColor: g.okBg, color: g.ok, border: `1px solid ${g.okLine}` },
        },
      },
    },
  });
};

// Default light theme export for backward compatibility
export const deeliverTheme = createDeeliverTheme('light');
